import { useEffect, useState } from 'react';

/**
 * The app's four places: the overview, one node's detail view, settings, and
 * the guide to what this app can track. A hash route rather than
 * `react-router` because the whole app is a single screen tree mounted well
 * below the actual document root (see
 * `App.tsx`, which this wave does not touch) — a hash needs no provider
 * wrapping anything above it, and it survives a hard refresh without any
 * server-side "serve index.html for unknown paths" setup, which is out of
 * scope here.
 *
 * Opening a node sets `location.hash`, which — like `pushState` — creates
 * a real history entry. The browser's own Back button then lands on the
 * previous hash (the overview) rather than leaving the app, which is the
 * whole point.
 */
export type Route =
  | { name: 'overview' }
  | { name: 'node'; nodeId: string }
  | { name: 'settings' }
  | { name: 'guide' };

const NODE_HASH = /^#\/nodes\/([^/]+)$/;

function parseHash(hash: string): Route {
  const match = NODE_HASH.exec(hash);
  if (match) return { name: 'node', nodeId: decodeURIComponent(match[1]!) };
  if (hash === '#/settings') return { name: 'settings' };
  if (hash === '#/guide') return { name: 'guide' };
  return { name: 'overview' };
}

export function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onHashChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return route;
}

/** Opens a node's detail view, pushing a history entry via the hash. */
export function navigateToNode(nodeId: string): void {
  window.location.hash = `/nodes/${encodeURIComponent(nodeId)}`;
}

/**
 * Returns to the overview deterministically.
 *
 * Deliberately not `history.back()`: a node link opened directly (a
 * refresh, a shared link) may have no earlier "overview" entry to go back to,
 * and `history.back()` would then leave the app rather than showing it. Going
 * to the empty hash always works, and — same as `navigateToNode` — still
 * pushes a real history entry, so the browser's own Back button keeps working
 * afterwards too.
 */
export function navigateToDashboard(): void {
  window.location.hash = '';
}

/** Opens settings, pushing a history entry so Back returns to the overview. */
export function navigateToSettings(): void {
  window.location.hash = '#/settings';
}

/**
 * Opens the node-type guide.
 *
 * A real hash, not a modal, so it can be linked to and returned to — the page
 * is a reference someone reads while deciding what to add, and a dialog that
 * evaporates on a stray click is the wrong shape for that.
 */
export function navigateToGuide(): void {
  window.location.hash = '#/guide';
}
